import { Listbox, Transition } from '@headlessui/react';
import CheckIcon from '@heroicons/react/solid/CheckIcon';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';

import clsxm from '@/lib/clsxm';

import { IconColor, IconSize } from '@/components/icons/base-icon';
import ChevronDownIcon from '@/components/icons/chevron-down-icon';
import Typography from '@/components/typography';

import COMMON_CLASS_NAMES from '@/constants/common-class-names';

export type TOption<V = string> = {
  id: string;
  label: string;
  value: V;
};

export type SelectProps<T extends FieldValues = TOption> = {
  name?: string;
  label?: string | ReactNode;
  placeholder?: string;
  options: T[];
  selectedOptions?: T | T[];
  uniqueKey: keyof T;
  labelKey: keyof T;
  valueKey: keyof T;
  onSelect?: (option: T | T[]) => void;
  multiple?: boolean;
  className?: string;
  labelClassName?: string;
  optionsClassName?: string;
  rounded?: boolean;
};

export default function Select<T extends FieldValues = TOption>({
  name,
  label,
  placeholder = 'Select...',
  options = [],
  selectedOptions: initialSelectedOptions,
  uniqueKey,
  labelKey,
  valueKey,
  onSelect,
  multiple = false,
  className,
  labelClassName,
  optionsClassName,
  rounded,
  ...props
}: SelectProps<T>) {
  const [selectedOptions, setSelectedOptions] = useState<T | T[] | undefined>(
    initialSelectedOptions
  );

  const handleChange = (option: T | T[]): void => {
    setSelectedOptions(option);
    if (onSelect) onSelect(option);
  };

  const isSelectedOption = (option: T): boolean => {
    const optionId = option[uniqueKey];
    if (multiple && Array.isArray(selectedOptions)) {
      return !!selectedOptions.find(
        (selectedOption: T) => selectedOption[uniqueKey] === optionId
      );
    }
    return !!(
      selectedOptions && (selectedOptions as T)[uniqueKey] === optionId
    );
  };

  const renderSelectedValuesDisplay = (option?: T | T[] | null) => {
    if (!option || option.length === 0 || Object.keys(option).length === 0) {
      return (
        <Typography.Body2 className='h-full text-grey600'>
          {placeholder}
        </Typography.Body2>
      );
    } else if (Array.isArray(option)) {
      return (
        <Typography.Body2>
          {option
            .map((option) => option[labelKey])
            .sort()
            .join(', ')}
        </Typography.Body2>
      );
    }
    return <Typography.Body2>{option[labelKey]}</Typography.Body2>;
  };

  const hiddenSelectValue = (formValue?: T | T[] | null) => {
    return Array.isArray(formValue)
      ? formValue.map((option) => String(option[valueKey]))
      : String(formValue && formValue[valueKey]);
  };

  useEffect(() => {
    // two-way binding
    setSelectedOptions(initialSelectedOptions);
  }, [initialSelectedOptions]);

  return (
    <Listbox
      defaultValue={selectedOptions}
      onChange={handleChange}
      multiple={multiple}
      {...props}
      value={selectedOptions || (multiple ? [] : null)}
    >
      {({ open, value: formValue }) => (
        <div
          data-testid='Select_Container'
          className='relative flex w-full flex-col'
        >
          <select
            hidden
            name={name}
            defaultValue={hiddenSelectValue(formValue)}
            multiple={multiple}
          >
            {options.map((option) => (
              <option key={option[uniqueKey]} value={option[valueKey] || ''}>
                {option[labelKey]}
              </option>
            ))}
          </select>

          <Listbox.Button
            data-testid='Select_inputContainer'
            className={clsxm(
              COMMON_CLASS_NAMES.input,
              COMMON_CLASS_NAMES.inputContainer,
              rounded && 'rounded-full',
              'flex flex-row items-center justify-between',
              className
            )}
          >
            <div className='flex flex-col items-start'>
              {label && (
                <Listbox.Label
                  data-testid='Select_label'
                  className={clsxm(
                    'mb-1 block text-sm font-medium text-gray-500',
                    labelClassName
                  )}
                >
                  <Typography.Subtitle5
                    as='label'
                    className={clsxm('text-grey700', labelClassName)}
                  >
                    {label}
                  </Typography.Subtitle5>
                </Listbox.Label>
              )}
              <div
                data-testid='Select_input'
                className='flex w-full flex-row items-center justify-between'
              >
                {renderSelectedValuesDisplay(formValue)}
              </div>
            </div>
            <span className='pointer-events-none inset-y-0 right-0 flex items-center'>
              <ChevronDownIcon
                size={IconSize.SMALL}
                color={IconColor.INHERIT}
                className={clsxm('self-start', open && 'rotate-180')}
              />
            </span>
          </Listbox.Button>

          <Transition
            show={open}
            as={Fragment}
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Listbox.Options
              data-testid='Select_optionsList'
              className={clsxm(
                'absolute top-[100%] z-10 mt-2 max-h-60 min-w-full overflow-auto rounded-md bg-white py-1 text-base',
                'shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
                optionsClassName
              )}
            >
              {options.map((option) => {
                const key = option[uniqueKey];
                const isSelected = isSelectedOption(option);
                return (
                  <Listbox.Option
                    key={key}
                    data-testid={`Select_optionsList_${key}`}
                    className={({ active }) =>
                      clsxm(
                        active ? 'bg-lighter-purple' : 'text-gray-900',
                        'relative cursor-default select-none px-6 py-4',
                        (active || isSelected) && 'bg-orchid-notification'
                      )
                    }
                    value={option}
                  >
                    {({ active }) => (
                      <Typography.Body2
                        className={clsxm(
                          'flex justify-between truncate hover:bg-lighter-purple',
                          active && 'bg-lighter-purple',
                          isSelected ? 'font-semibold' : 'font-normal'
                        )}
                      >
                        {option[labelKey]}
                        <span>
                          {isSelected && (
                            <CheckIcon
                              className='h-5 w-5 text-orchid'
                              aria-hidden='true'
                            />
                          )}
                        </span>
                      </Typography.Body2>
                    )}
                  </Listbox.Option>
                );
              })}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
